<script setup lang="ts">
import { resizeImage } from '~/shared/lib/resizeImage';
import { Swiper, SwiperSlide } from 'swiper/vue';
import type { Swiper as SwiperClass } from 'swiper';
import { Navigation } from 'swiper/modules';
import type { NavigationOptions } from 'swiper/types';
import {
  CLIENT_PUBLISHER_SPECIAL_YEAR_KEY,
  CLIENT_PUBLISHER_SPECIAL_YEAR_URL,
} from '~/shared/api/client/publisher/publisher.const';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { newsTypesArticle } from '~/entities/special-project-chronicles-year/lib/const';
import type { IPublisherDataClientDTO } from '~/shared/api/client/publisher/publisher.dto';
import type { ISpecialProjectYearDataClientDTO } from '~/shared/api/client/special_project_year/special_project_year.dto';
import { reachGoal } from '~/shared/utils/reachGoal';

const swiperRef = ref<{ swiper: SwiperClass } | null>(null);
const navigationOptions = ref<NavigationOptions>({
  prevEl: null,
  nextEl: null,
});
const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);
const isControlVisible = ref<boolean>(true);

const breakpoints = 3;

const { data, status } = await useCustomFetch<IPublisherDataClientDTO[]>(
  CLIENT_PUBLISHER_SPECIAL_YEAR_URL,
  {
    method: 'GET',
    key: CLIENT_PUBLISHER_SPECIAL_YEAR_KEY,
  },
);

const categoryLink = computed(() =>
  data.value?.[0]?.tag_id ? `/category/${data.value?.[0]?.tag_id}` : false,
);

const utmAddition =
  '?utm_source=cre&utm_medium=spec_chronicle&utm_campaign=inner';

const sentinelRef = ref<HTMLElement | null>(null);
let blockTriggered = false;
const observer = ref<IntersectionObserver | null>(null);

onMounted(() => {
  if (prevEl.value && nextEl.value) {
    navigationOptions.value = {
      prevEl: prevEl.value,
      nextEl: nextEl.value,
    };
  }

  if (import.meta.client) {
    observer.value = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Низ шапки
          if (
            entry.target === sentinelRef.value &&
            entry.isIntersecting &&
            !blockTriggered
          ) {
            blockTriggered = true;
            reachGoal('inner_spec_chronicle');
          }
        });
      },
      {
        threshold: [0, 1], // срабатывает, когда sentinel появляется
      },
    );

    if (sentinelRef.value) observer.value.observe(sentinelRef.value);
  }

  watch(
    () => isControlVisible,
    (newValue) => {
      const swiper = swiperRef.value?.swiper;
      if (!swiper) return;

      if (!newValue) {
        swiper.navigation?.destroy();
      } else {
        const swiper = swiperRef.value?.swiper;
        if (swiper && isControlVisible && swiper.navigation) {
          nextTick(() => {
            if (prevEl.value && nextEl.value) {
              swiper.navigation.destroy();
              swiper.navigation.init();
              swiper.navigation.update();
            }
          });
        }
      }
    },
  );
});

onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
});
</script>

<template>
  <template
    v-if="
      status === 'success' &&
      data &&
      data[0] &&
      data[0].related_publishers.length > 0 &&
      data[0]?.show_special_project_chronicles
    "
  >
    <div :class="$style.root">
      <div
        :class="$style.cre"
        :style="{
          '--background-color': data[0].background_color as string,
          '--text-color': data[0].text_color as string,
          '--border-color': data[0].border_color as string,
          '--label-color': data[0].label_color as string,
          '--substrates-color': data[0].substrates_color as string,
        }"
      >
        <div :class="$style.cre__header">
          <div :class="$style.cre__header__inner">
            <div :class="$style.cre__header__titleWrapper">
              <NuxtLink
                v-if="data[0].tag && categoryLink"
                :class="$style.cre__header__tagWrapper"
                :to="`${categoryLink}${utmAddition}`"
              >
                <div :class="$style.cre__header__subtitle">Спецвыпуск</div>
                <img
                  v-if="data[0].tag.image_category"
                  :src="
                    resizeImage(data[0].tag.image_category.file_url, {
                      width: 140,
                    })
                  "
                  alt="Tag"
                  :class="$style.cre__header__tag"
                />
              </NuxtLink>
              <div
                :class="$style.cre__header__title"
                :style="{
                  fontSize: data[0].font_size_title?.trim() || undefined,
                }"
              >
                {{ data[0].title_block }}
              </div>
            </div>
            <div
              v-if="data[0].text_two_block && data[0].image_title_block"
              :class="$style.cre__header__partnerWrapper"
            >
              <div
                :class="[
                  $style.cre__header__subtitle,
                  $style.cre__header__subtitle_margin,
                ]"
              >
                {{ data[0].text_two_block }}
              </div>
              <NuxtLink
                v-if="data[0].image_title_block && data[0].link_partner"
                external
                target="_blank"
                :to="`${data[0].link_partner}${utmAddition}`"
                :class="$style.cre__header__partnerWrapperInner"
              >
                <img
                  :src="
                    resizeImage(data[0].image_title_block.file_url, {
                      width: 400,
                    })
                  "
                  :class="$style.cre__header__partner"
                  alt="Partner"
                />
              </NuxtLink>
              <div
                v-else-if="data[0].image_title_block"
                :class="$style.cre__header__partnerWrapperInner"
              >
                <img
                  :src="
                    resizeImage(data[0].image_title_block.file_url, {
                      width: 400,
                    })
                  "
                  :class="$style.cre__header__partner"
                  alt="Partner"
                />
              </div>
            </div>
          </div>

          <div :class="$style.cre__header__navigation">
            <Button
              v-if="data[0].text_block_button && categoryLink"
              :class="$style.cre__header__button"
              :to="`${categoryLink}${utmAddition}`"
            >
              {{ data[0].text_two_block_button }}
            </Button>
            <div
              ref="prevEl"
              :class="$style.cre__header__controlsItem"
              :style="{ borderColor: data[0].border_color as string }"
            >
              <Icon
                :class="$style.slider__controlsIcon"
                name="my-icon:i-arrow-left"
              />
            </div>
            <div
              ref="nextEl"
              :class="$style.cre__header__controlsItem"
              :style="{ borderColor: data[0].border_color as string }"
            >
              <Icon
                :class="$style.slider__controlsIcon"
                name="my-icon:i-arrow-right"
              />
            </div>
          </div>
        </div>
        <div ref="sentinelRef" />
        <div :class="$style.content">
          <Swiper
            ref="swiperRef"
            :observer="true"
            :observe-parents="true"
            :class="$style.slider__swiperContainer"
            :modules="[Navigation]"
            :grab-cursor="true"
            :loop="true"
            :navigation="navigationOptions"
            :style="{ borderColor: data[0].border_color as string }"
            :slides-per-view="breakpoints ? 1 : 'auto'"
            :slides-per-group="1"
            :breakpoints="
              breakpoints
                ? {
                    768: {
                      slidesPerView: 2,
                    },
                    1380: {
                      slidesPerView: 3,
                    },
                  }
                : {}
            "
          >
            <SwiperSlide
              v-for="(item, key) in data[0]
                .related_publishers as ISpecialProjectYearDataClientDTO[]"
              :key="`articleSpecial_${key}`"
              :style="{ borderColor: data[0].border_color as string }"
              :class="$style.swiperSlide"
            >
              <component
                :is="
                  newsTypesArticle[
                    item.publisher_news[0]
                      ?.type_item as keyof typeof newsTypesArticle
                  ]
                "
                v-bind="item.publisher_news[0]!"
                :class="[$style.grid__newsItem]"
                :image_one="
                  item.publisher_news[0]?.image_one ||
                  item.publisher_news[0]?.news?.image?.photo ||
                  null
                "
                :background-color="data[0].background_color"
                :text-color="data[0].text_color as string"
                :utm="utmAddition"
                :internal="true"
              />
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
    </div>
  </template>
</template>

<style module lang="scss">
.root {
  margin-left: -110px;
  margin-right: -110px;
}

.cre {
  background: var(--substrates-color);
  border-bottom: var(--background-color) solid 10px;

  &__header {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    height: 72px;
    justify-content: space-between;
    background: var(--background-color);

    &__inner {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      height: 100%;
      width: 720px;
      flex-shrink: 0;
      min-height: 28px;
      justify-content: space-between;
      padding-left: var(--space-2-5x);
      padding-right: var(--space-2-5x);
      border-right: 1px solid var(--border-color);
    }

    &__navigation {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      height: 100%;
      width: 360px;
    }

    &__button {
      @include text_button3;
      width: 160px;
      height: 100%;
      color: var(--color-black-1);
      padding: 0;
    }

    &__controlsItem {
      display: flex;
      width: 100px;
      height: 100%;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      border-left: 1px solid;
      flex-shrink: 0;

      path {
        fill: var(--color-black-1);
      }
    }

    &__partner {
      margin-left: 12px;

      &Wrapper {
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        gap: var(--space-1x);

        &Inner {
          display: flex;
        }
      }
    }

    &__title {
      font-family: var(--font-golos);
      font-weight: 600;
      font-size: 24px;
      line-height: 110%;
      color: var(--text-color);

      &Wrapper {
        display: flex;
        flex-flow: column nowrap;
        gap: 1px;
      }
    }

    &__subtitle {
      @include text_caption2_caps;
      line-height: 140%;
      color: var(--label-color);
      &_margin {
        margin-left: auto;
      }
    }

    &__tagWrapper {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      gap: var(--space-1x);
      text-decoration: none;
    }

    &__tag {
      width: 70px;
      height: auto;
    }
  }
}

.swiperSlide {
  width: 270px;
  height: auto;
  padding: var(--space-2-5x);
  border-right: 1px solid;
}

.grid__newsItem {
  text-decoration: none;
}

@media (max-width: 1379px) {
  .root {
    margin-left: -20px;
    margin-right: -20px;
  }

  .cre {
    &__header {
      flex-direction: column;
      height: inherit;

      &__inner {
        width: 100%;
        height: 72px;
        border-right: none;
      }

      &__navigation {
        border-top: 1px solid var(--border-color);
        height: 64px;
        width: 100%;
      }

      &__button {
        flex-grow: 1;
        width: inherit;
      }

      &__partner {
        &Wrapper {
          align-self: normal;
          justify-content: space-between;
        }
      }
    }
  }
}

@media (max-width: 767px) {
  .swiperSlide {
    border-right: none;
    width: 100%;
  }

  .cre {
    &__header {
      &__inner {
        width: 100%;
        padding: var(--space-2-5x);
        align-items: flex-start;
        height: inherit;
        flex-direction: column;
        gap: var(--space-2-5x);
      }

      &__partner {
        margin: 0;

        &Wrapper {
          flex-direction: row;
          gap: var(--space-1x);
          align-self: flex-start;
        }
      }
    }
  }
}
</style>
